import type {
  AreaUpdate,
  Line,
  MatomoConfig,
  NetworkInfo,
  Product,
  TripDetail,
  Vehicle,
} from './types';

type WireLine = [string, string, string, string, string | null];
type WireVehicle = [
  string,
  number,
  number,
  number | null,
  number,
  string | null,
  number | null,
  string | null,
  number | null,
  0 | 1,
  0 | 1,
  number,
];

interface WireArea {
  networks: string[];
  updatedAt: number;
  fetchedAt: number;
  stale: boolean;
  error: string | null;
  notice: string | null;
  lines: WireLine[];
  vehicles: WireVehicle[];
}

export interface StopHit {
  id: string;
  name: string;
  area: string | null;
  lat: number;
  lon: number;
}

export interface BBox {
  south: number;
  west: number;
  north: number;
  east: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, { signal, headers: { accept: 'application/json' } });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      // Keep the status-only message.
    }
    throw new ApiError(message, res.status);
  }
  return (await res.json()) as T;
}

export async function fetchNetworks(
  signal?: AbortSignal,
): Promise<{ networks: NetworkInfo[]; pollIntervalMs: number; matomo: MatomoConfig | null }> {
  return getJson('/api/networks', signal);
}

export async function fetchArea(
  bbox: BBox,
  limit: number,
  signal?: AbortSignal,
): Promise<AreaUpdate> {
  const query = new URLSearchParams({
    bbox: [bbox.south, bbox.west, bbox.north, bbox.east].map((n) => n.toFixed(4)).join(','),
    limit: String(limit),
  });
  const wire = await getJson<WireArea>(`/api/vehicles?${query}`, signal);

  const lines: Line[] = wire.lines.map(([name, product, bg, fg, operator]) => ({
    name,
    product: product as Product,
    bg,
    fg,
    operator,
  }));
  const fallback: Line = { name: '?', product: 'other', bg: '#5b6472', fg: '#ffffff', operator: null };

  const vehicles: Vehicle[] = wire.vehicles.map((v) => ({
    id: v[0],
    lat: v[1],
    lon: v[2],
    bearing: v[3],
    line: lines[v[4]] ?? fallback,
    direction: v[5],
    delay: v[6],
    nextStop: v[7],
    nextStopTime: v[8] === null ? null : v[8] * 1000,
    positionKind: v[9] === 1 ? 'estimated' : 'measured',
    scheduledOnly: v[10] === 1,
    network: wire.networks?.[v[11]] ?? '?',
  }));

  return {
    updatedAt: wire.updatedAt,
    fetchedAt: wire.fetchedAt,
    stale: wire.stale,
    error: wire.error,
    notice: wire.notice ?? null,
    networks: wire.networks ?? [],
    vehicles,
  };
}

export async function fetchStops(
  query: string,
  near: [number, number],
  signal?: AbortSignal,
): Promise<StopHit[]> {
  const params = new URLSearchParams({
    q: query,
    near: `${near[0].toFixed(4)},${near[1].toFixed(4)}`,
  });
  const body = await getJson<{ stops: StopHit[] }>(`/api/search?${params}`, signal);
  return body.stops ?? [];
}

export async function fetchTrip(
  network: string,
  tripId: string,
  signal?: AbortSignal,
): Promise<TripDetail> {
  const query = new URLSearchParams({ network, id: tripId });
  return getJson<TripDetail>(`/api/trip?${query}`, signal);
}
